import { defineStore } from 'pinia'
import { STORAGE_ENTRY_PARAMS_KEY } from '@/constants/h5-entry'
import type { H5EntryParams, H5EntryParamsPatch } from '@/types/h5-entry'

/**
 * 创建一个空的入口参数对象，所有字段都为 null。
 * 用于初始化和重置 Pinia Store 的默认状态。
 */
function createEmptyEntryParams(): H5EntryParams {
  return {
    sceneId: null,        // 场景 ID
    playerSceneId: null,  // 玩家场景 ID
    materialKey: null,    // 素材 key（本地动画映射）
    capturedUrl: null,    // 抓拍图片 url（productUrl 回退）
    productUrl: null,     // 成品图 url（优先作为 fill-slot productUrl）
    playType: null,       // 玩法类型
    entry: null,          // 入口意图（如 continue/view-result）
    shareToken: null,     // 分享 token
    joinedSlotKey: null,  // CONTRIBUTOR 预分配槽
  }
}

/**
 * 小程序 web-view 注入的 H5 入口参数 Pinia Store。
 * 支持参数 Patch（增量覆盖）、重置，以及自动持久化到 sessionStorage。
 */
export const useEntryParamsStore = defineStore('entryParams', {
  // 初始状态：所有参数为空
  state: (): H5EntryParams => createEmptyEntryParams(),
  actions: {
    /**
     * Patch 部分入口参数，忽略未提供的字段，避免覆盖为 undefined。
     * @param params 入口参数补丁对象 Partial<H5EntryParams>
     */
    patch(params: H5EntryParamsPatch): void {
      if (params.sceneId != null) {
        this.sceneId = params.sceneId
      }
      if (params.playerSceneId != null) {
        this.playerSceneId = params.playerSceneId
      }
      if (params.materialKey != null) {
        this.materialKey = params.materialKey
      }
      if (params.capturedUrl != null) {
        this.capturedUrl = params.capturedUrl
      }
      if (params.productUrl != null) {
        this.productUrl = params.productUrl
      }
      if (params.playType != null) {
        this.playType = params.playType
      }
      if (params.entry != null) {
        this.entry = params.entry
      }
      if (params.shareToken != null) {
        this.shareToken = params.shareToken
      }
      if (params.joinedSlotKey != null) {
        this.joinedSlotKey = params.joinedSlotKey
      }
    },
    /**
     * 重置入口参数为默认（全部为 null）。
     */
    reset(): void {
      Object.assign(this, createEmptyEntryParams())
    },
    /** 清除填槽相关残留（查看结果入口时避免幽灵托盘） */
    clearFillParams(): void {
      this.materialKey = null
      this.capturedUrl = null
      this.productUrl = null
      this.joinedSlotKey = null
    },
  },
  // 会话级持久化，自动同步到 sessionStorage
  persist: {
    key: STORAGE_ENTRY_PARAMS_KEY,
    storage: sessionStorage,
  },
})
